import React, { useState, useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Maximize2, Minimize2, X, Download, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { presentationData, propertyData, brandAssets } from '../../data/mock';

const PresentationSection = ({ onInquire }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [imageLoaded, setImageLoaded] = useState({});
  
  const totalSlides = presentationData.totalSlides;
  const slideImages = presentationData.slideImages;

  const goToSlide = useCallback((index) => {
    if (index >= 0 && index < totalSlides) {
      setCurrentSlide(index);
    }
  }, [totalSlides]);

  const goNext = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  }, [totalSlides]);

  const goPrev = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'Escape' && isFullscreen) setIsFullscreen(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goNext, goPrev, isFullscreen]);

  // Touch/swipe navigation
  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (!touchStart) return;
    
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;
    
    if (Math.abs(diff) > 50) {
      if (diff > 0) goNext();
      else goPrev();
    }
    
    setTouchStart(null);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleDownloadPDF = () => {
    // Create a hidden anchor tag to trigger download
    const link = document.createElement('a');
    link.href = presentationData.pdfUrl;
    link.download = 'William-Lane-Estate-Presentation.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleEmailRequest = () => {
    const subject = encodeURIComponent(`Request: ${propertyData.brandName} Presentation Package`);
    const body = encodeURIComponent(
      `I am interested in receiving the complete information package for:\n\n` +
      `${propertyData.brandName}\n` +
      `${propertyData.address}\n` +
      `Offered at ${propertyData.priceShort}\n\n` +
      `Please send me:\n` +
      `- The full estate presentation\n` +
      `- Property details and specifications\n` +
      `- Information on scheduling a private viewing\n\n` +
      `Thank you.`
    );
    window.location.href = `mailto:${propertyData.estateContact.email}?subject=${subject}&body=${body}`;
  };

  const handleImageLoad = (index) => {
    setImageLoaded(prev => ({ ...prev, [index]: true }));
  };

  const isLastSlide = currentSlide === totalSlides - 1;

  // Navigation controls component
  const NavigationControls = ({ isFullscreenMode = false }) => (
    <div className={`flex items-center justify-between ${isFullscreenMode ? 'absolute bottom-8 left-8 right-8' : 'mt-6'}`}>
      {/* Previous Button */}
      <button
        onClick={goPrev}
        className={`rounded-full bg-white/10 hover:bg-amber-500/30 hover:border-amber-500 border-2 border-white/20 flex items-center justify-center text-white transition-all ${
          isFullscreenMode ? 'w-16 h-16' : 'w-14 h-14'
        }`}
        aria-label="Previous slide"
        data-testid="presentation-prev-btn"
      >
        <ChevronLeft className={isFullscreenMode ? 'w-10 h-10' : 'w-8 h-8'} />
      </button>

      {/* Slide Counter & Dots */}
      <div className="flex items-center space-x-4">
        <span className="text-white font-medium text-lg">
          {currentSlide + 1} / {totalSlides}
        </span>
        
        {/* Dot indicators (desktop only, show subset) */}
        <div className="hidden md:flex items-center space-x-1.5">
          {Array.from({ length: Math.min(7, totalSlides) }, (_, idx) => {
            let actualIndex;
            if (totalSlides <= 7) {
              actualIndex = idx;
            } else if (currentSlide < 3) {
              actualIndex = idx;
            } else if (currentSlide > totalSlides - 4) {
              actualIndex = totalSlides - 7 + idx;
            } else {
              actualIndex = currentSlide - 3 + idx;
            }
            
            return (
              <button
                key={actualIndex}
                onClick={() => goToSlide(actualIndex)}
                className={`rounded-full transition-all ${
                  currentSlide === actualIndex 
                    ? 'bg-amber-400 w-8 h-2' 
                    : 'bg-white/30 hover:bg-white/50 w-2 h-2'
                }`}
                aria-label={`Go to slide ${actualIndex + 1}`}
              />
            );
          })}
        </div>
      </div>

      {/* Next Button */}
      <button
        onClick={goNext}
        className={`rounded-full bg-white/10 hover:bg-amber-500/30 hover:border-amber-500 border-2 border-white/20 flex items-center justify-center text-white transition-all ${
          isFullscreenMode ? 'w-16 h-16' : 'w-14 h-14'
        }`}
        aria-label="Next slide"
        data-testid="presentation-next-btn"
      >
        <ChevronRight className={isFullscreenMode ? 'w-10 h-10' : 'w-8 h-8'} />
      </button>
    </div>
  );

  return (
    <section id="presentation" className="py-24 bg-gradient-to-b from-[#0d1117] to-[#0a0c10]" data-testid="presentation-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="inline-block px-4 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-400 text-sm font-medium tracking-wide mb-4">
            Estate Presentation
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white mb-4">
            THE WILLIAM LANE ESTATE PRESENTATION
          </h2>
          <p className="text-lg text-white/70 font-light">
            Explore the complete estate overview. Use arrows or swipe to navigate. Download the full presentation below.
          </p>
        </div>

        {/* Presentation Carousel */}
        <div className="relative">
          {/* Main Slide Container */}
          <div 
            className="relative bg-gradient-to-br from-[#1a1f2e] to-[#0d1117] border border-white/10 rounded-2xl overflow-hidden"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {/* Fullscreen Toggle */}
            <button
              onClick={toggleFullscreen}
              className="absolute top-4 right-4 z-10 w-12 h-12 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-sm flex items-center justify-center text-white transition-all"
              aria-label="Toggle fullscreen"
              data-testid="presentation-fullscreen-btn"
            >
              <Maximize2 className="w-6 h-6" />
            </button>

            {/* Slide Image */}
            <div className="relative aspect-[16/9] flex items-center justify-center bg-black">
              {/* Loading placeholder */}
              {!imageLoaded[currentSlide] && (
                <div className="absolute inset-0 flex items-center justify-center bg-[#1a1f2e]">
                  <div className="w-12 h-12 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
                </div>
              )}
              
              <img
                src={slideImages[currentSlide]}
                alt={`Presentation slide ${currentSlide + 1} of ${totalSlides}`}
                className={`max-w-full max-h-full object-contain transition-opacity duration-300 ${
                  imageLoaded[currentSlide] ? 'opacity-100' : 'opacity-0'
                }`}
                onLoad={() => handleImageLoad(currentSlide)}
                data-testid={`presentation-slide-${currentSlide + 1}`}
              />
            </div>

            {/* Slide indicator overlay */}
            <div className="absolute bottom-4 left-4 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-1.5">
              <span className="text-white/80 text-sm">Slide {currentSlide + 1} of {totalSlides}</span>
            </div>
          </div>

          {/* Navigation Controls */}
          <NavigationControls />

          {/* Download & CTA Section */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={handleDownloadPDF}
              variant="outline"
              size="lg"
              className="w-full sm:w-auto border-amber-500/50 text-amber-400 hover:bg-amber-500/10 hover:border-amber-500 px-8"
              data-testid="download-presentation-btn"
            >
              <Download className="w-5 h-5 mr-2" />
              Download Full Presentation (PDF)
            </Button>
            
            <Button
              onClick={handleEmailRequest}
              size="lg"
              className="w-full sm:w-auto bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-8 shadow-xl shadow-amber-500/30"
              data-testid="email-presentation-btn"
            >
              <Mail className="w-5 h-5 mr-2" />
              Request Information Package
            </Button>
          </div>

          {/* CTA after last slide */}
          {isLastSlide && (
            <div className="mt-8 text-center animate-in fade-in duration-500">
              <p className="text-white/70 mb-4">Ready to learn more about this exceptional estate?</p>
              <Button
                onClick={onInquire}
                size="lg"
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-10 py-6 text-lg shadow-2xl shadow-amber-500/30"
                data-testid="presentation-cta-btn"
              >
                REQUEST PRIVATE ESTATE INFORMATION
              </Button>
            </div>
          )}
        </div>

        {/* Contact Info */}
        <div className="mt-8 text-center">
          <p className="text-white/50 text-sm">
            For inquiries: <a href={`mailto:${propertyData.estateContact.email}`} className="text-amber-400 hover:text-amber-300">{propertyData.estateContact.email}</a>
          </p>
        </div>

        {/* Keyboard hint */}
        <p className="text-center text-white/40 text-xs mt-4">
          Use ← → arrow keys or swipe to navigate • Press fullscreen for immersive view
        </p>
      </div>

      {/* Fullscreen Modal */}
      {isFullscreen && (
        <div className="fixed inset-0 z-[100] bg-black flex flex-col" data-testid="fullscreen-presentation">
          {/* Close Button */}
          <button
            onClick={() => setIsFullscreen(false)}
            className="absolute top-4 right-4 z-10 w-14 h-14 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all"
            aria-label="Close fullscreen"
          >
            <X className="w-8 h-8" />
          </button>

          {/* Download Button in Fullscreen */}
          <button
            onClick={handleDownloadPDF}
            className="absolute top-4 left-4 z-10 flex items-center space-x-2 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/50 rounded-full px-4 py-2 text-amber-400 transition-all"
          >
            <Download className="w-5 h-5" />
            <span className="text-sm font-medium">Download PDF</span>
          </button>

          {/* Fullscreen Slide */}
          <div 
            className="flex-1 flex items-center justify-center p-4 md:p-8"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div className="relative max-w-6xl w-full h-full flex items-center justify-center">
              <img
                src={slideImages[currentSlide]}
                alt={`Presentation slide ${currentSlide + 1} of ${totalSlides}`}
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              />
            </div>
          </div>

          {/* Fullscreen Navigation */}
          <div className="pb-8">
            <NavigationControls isFullscreenMode />
          </div>

          {/* Contact in Fullscreen */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2">
            <p className="text-white/40 text-xs">
              {propertyData.estateContact.email}
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

export default PresentationSection;
